/**
 * Make text checklists interactive.
 *
 * A Proteus document can carry a checklist authored as plain text — a ballot-box
 * glyph (☐/☑) rather than an interactive field — in which case the boxes don't
 * toggle. This transform swaps each glyph for a checkbox `Input` node bound by
 * `name`, and returns the per-name initial checked state. The card detects a
 * checklist (any names produced) and renders it with StickyChecklist (Axiom's
 * real Checkbox — Proteus draws a checkbox Input as a toggle), persisting via
 * `user_card_data`. It only touches the glyph cell — the label and any badge
 * stay where they were authored, so the layout is unchanged. Documents with no
 * glyphs (real toggle/Switch cards, plain text, TipTap) pass through untouched.
 *
 * Two shapes are handled:
 *  - a row whose first cell is a glyph-only `Text`, with the label in a sibling
 *    (`Group[ Text "☐", Group[ Text "Do X", Badge ] ]`) → the `Text "☐"` becomes
 *    a bare checkbox `Input`; the sibling column is left alone; and
 *  - an inline `Text` that starts with a glyph (`Text "☐ Do X"`, a markdown
 *    checklist line) → a `Group` of a checkbox `Input` + its label `Text`.
 */

const UNCHECKED = new Set(['☐', '⬜', '▢', '◻', '🔲', '🔳']);
const CHECKED = new Set(['☑', '☒', '✅', '✔', '🗹']);
const VARIATION_SELECTOR = /[︎️]/g;

/**
 * If `s` starts with a checkbox glyph, return its checked state and the rest of
 * the string (the inline label) with the glyph + any variation selector
 * stripped. Otherwise `state: null`.
 */
function parseGlyph(s: string): { state: boolean | null; rest: string } {
  const trimmed = s.trim();
  const cp = [...trimmed][0]; // first code point (handles surrogate-pair emoji)
  if (!cp) return { state: null, rest: '' };
  const state = CHECKED.has(cp) ? true : UNCHECKED.has(cp) ? false : null;
  if (state === null) return { state: null, rest: '' };
  const rest = trimmed.slice(cp.length).replace(VARIATION_SELECTOR, '').trim();
  return { state, rest };
}

type Node = Record<string, unknown>;

function isText(n: unknown): n is { $type: 'Text'; children: string } {
  return (
    !!n &&
    typeof n === 'object' &&
    (n as Node).$type === 'Text' &&
    typeof (n as Node).children === 'string'
  );
}

/** A glyph-only Text cell (the box, with no inline label). */
function isGlyphOnlyText(n: unknown): n is { $type: 'Text'; children: string } {
  if (!isText(n)) return false;
  const { state, rest } = parseGlyph(n.children);
  return state !== null && rest === '';
}

export type ChecklistTransform = {
  /** The document with glyph checklists rewritten to interactive checkbox inputs. */
  doc: unknown;
  /** Initial checked state per generated field `name` (from the glyph). */
  initialChecks: Record<string, boolean>;
};

/**
 * Rewrite text checklists in a Proteus document into interactive checkbox
 * `Input`s. Pure — the input is never mutated. Returns the new doc plus the
 * initial checked state (so the card can seed its data from the glyphs).
 */
export function transformChecklist(card: unknown): ChecklistTransform {
  const initialChecks: Record<string, boolean> = {};
  let counter = 0;

  // A real checkbox is a Proteus `Input` of type "checkbox" bound by `name`
  // (Proteus has no `Checkbox` element; `Switch` is the toggle the user didn't want).
  const makeCheckbox = (checked: boolean): Node => {
    const name = `check_${counter++}`;
    initialChecks[name] = checked;
    return { $type: 'Input', type: 'checkbox', name };
  };

  const walk = (node: unknown): unknown => {
    if (Array.isArray(node)) return node.map(walk);
    if (!node || typeof node !== 'object') return node;
    const n = node as Node;

    // Row whose first cell is a glyph-only Text → swap just that cell for a bare
    // checkbox; keep (and recurse) the label/badge sibling so layout is intact.
    if (n.$type === 'Group' && Array.isArray(n.children) && isGlyphOnlyText(n.children[0])) {
      const checked = parseGlyph((n.children[0] as { children: string }).children).state === true;
      return {
        ...n,
        children: [makeCheckbox(checked), ...(n.children as unknown[]).slice(1).map(walk)],
      };
    }

    // Inline "☐ label" Text → a checkbox + its label, in a row.
    if (isText(n)) {
      const { state, rest } = parseGlyph(n.children);
      if (state !== null && rest) {
        return {
          $type: 'Group',
          flexDirection: 'row',
          gap: '8',
          alignItems: 'center',
          children: [makeCheckbox(state), { $type: 'Text', children: rest }],
        };
      }
      return n;
    }

    if (Array.isArray(n.children)) return { ...n, children: (n.children as unknown[]).map(walk) };
    if (Array.isArray(n.body)) return { ...n, body: (n.body as unknown[]).map(walk) };
    return n;
  };

  return { doc: walk(card), initialChecks };
}
